'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const programs = [
  {
    id: 'dpharm',
    tag: 'Diploma',
    tagColor: 'tag-green',
    title: 'D.Pharm',
    subtitle: 'Diploma in Pharmacy',
    duration: '2 Years',
    description:
      'A foundational diploma program approved by PCI and BTE, preparing students for pharmacy practice with hands-on laboratory training.',
    highlights: [
      'Drug Dispensing',
      'Clinical Pharmacy',
      'Hospital Training',
    ],
    image:
      'https://img.rocket.new/generatedImages/rocket_gen_img_1cc77b8a6-1768683532632.png',
    imageAlt:
      'Pharmacy dispensing counter with medicine bottles, bright clinical environment',
    comingSoon: false,
  },
  {
    id: 'bpharm',
    tag: 'Bachelor',
    tagColor: 'tag-blue',
    title: 'B.Pharm',
    subtitle: 'Bachelor of Pharmacy',
    duration: '4 Years',
    description:
      'A comprehensive undergraduate program blending pharmaceutical sciences with research methodology and clinical exposure.',
    highlights: [
      'Pharmaceutical Chemistry',
      'Pharmacology',
      'Drug Research',
    ],
    image:
      'https://img.rocket.new/generatedImages/rocket_gen_img_1a6968d1a-1784117603831.png',
    imageAlt:
      'Modern pharmacy research laboratory with scientific equipment',
    comingSoon: true,
  },
];

export default function ProgramsPreview() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards =
              entry.target.querySelectorAll<HTMLElement>(
                '.prog-card'
              );

            cards.forEach((card, index) => {
              card.style.transitionDelay = `${index * 0.12}s`;
              card.style.opacity = '1';
              card.style.transform = 'translateY(0)';
            });

            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardsRef.current) {
      observer.observe(cardsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/components/ProgramsPreview.tsx:85:4" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="85" data-component-end-line="243" data-component-file="ProgramsPreview.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-20%20md%3Apy-28%20px-6%20bg-muted%22%7D" className="py-20 md:py-28 px-6 bg-muted">
      <div data-component-id="src/app/components/ProgramsPreview.tsx:86:6" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="86" data-component-end-line="242" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">

        {/* Header */}
        <div data-component-id="src/app/components/ProgramsPreview.tsx:89:8" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="89" data-component-end-line="110" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20md%3Aflex-row%20justify-between%20items-start%20md%3Aitems-end%20gap-6%20mb-14%22%7D" className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-14">
          <div data-component-id="src/app/components/ProgramsPreview.tsx:90:10" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="90" data-component-end-line="101" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <span data-component-id="src/app/components/ProgramsPreview.tsx:91:12" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="91" data-component-end-line="93" data-component-file="ProgramsPreview.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22section-label%20block%20mb-3%22%2C%22textContent%22%3A%22Academic%20Programs%22%7D" className="section-label block mb-3">
              Academic Programs
            </span>

            <h2 data-component-id="src/app/components/ProgramsPreview.tsx:95:12" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="95" data-component-end-line="100" data-component-file="ProgramsPreview.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-display%20text-foreground%22%2C%22textContent%22%3A%22Courses%20Designed%20for%22%7D" className="text-display text-foreground">
              Courses Designed for{' '}
              <em data-component-id="src/app/components/ProgramsPreview.tsx:97:14" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="97" data-component-end-line="99" data-component-file="ProgramsPreview.tsx" data-component-name="em" data-component-content="%7B%22elementName%22%3A%22em%22%2C%22className%22%3A%22font-normal%20italic%20text-primary%22%2C%22textContent%22%3A%22Tomorrow%22%7D" className="font-normal italic text-primary">
                Tomorrow
              </em>
            </h2>
          </div>

          <Link data-component-id="src/app/components/ProgramsPreview.tsx:103:10" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="103" data-component-end-line="109" data-component-file="ProgramsPreview.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fprograms%22%2C%22className%22%3A%22btn-outline%20shrink-0%22%2C%22textContent%22%3A%22View%20All%20Programs%22%7D"
            href="/programs"
            className="btn-outline shrink-0"
          >
            View All Programs
            <Icon data-component-id="src/app/components/ProgramsPreview.tsx:108:12" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="108" data-component-end-line="108" data-component-file="ProgramsPreview.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%7D" name="ArrowRightIcon" size={16} />
          </Link>
        </div>


        {/* Cards */}
        <div data-component-id="src/app/components/ProgramsPreview.tsx:114:8" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="114" data-component-end-line="240" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-8%22%7D"
          ref={cardsRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {programs.map((prog) => (
            <div data-component-id="src/app/components/ProgramsPreview.tsx:119:12" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="119" data-component-end-line="238" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={prog.id}
              className={`relative overflow-hidden rounded-2xl border border-border bg-card prog-card ${
                prog.comingSoon
                  ? 'pointer-events-none'
                  : ''
              }`}
              style={{
                opacity: 0,
                transform: 'translateY(40px)',
                transition:
                  'opacity .8s ease, transform .8s ease',
              }}
            >

              {/* Card Content */}
              <div data-component-id="src/app/components/ProgramsPreview.tsx:135:14" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="135" data-component-end-line="211" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={
                  prog.comingSoon
                    ? 'blur-[2px] saturate-50 brightness-90 transition-all duration-500'
                    : ''
                }
              >

                {/* Image */}
                <div data-component-id="src/app/components/ProgramsPreview.tsx:144:16" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="144" data-component-end-line="167" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20h-52%20overflow-hidden%22%7D" className="relative h-52 overflow-hidden">
                  <AppImage data-component-id="src/app/components/ProgramsPreview.tsx:145:18" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="145" data-component-end-line="155" data-component-file="ProgramsPreview.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%7D"
                    src={prog.image}
                    alt={prog.imageAlt}
                    fill
                    className={`object-cover transition-transform duration-700 ${
                      prog.comingSoon
                        ? 'grayscale'
                        : 'hover:scale-105'
                    }`}
                    sizes="(max-width:768px) 100vw, 50vw"
                  />


                  {!prog.comingSoon && (
                    <div data-component-id="src/app/components/ProgramsPreview.tsx:159:20" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="159" data-component-end-line="165" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-5%20left-5%22%7D" className="absolute top-5 left-5">
                      <span data-component-id="src/app/components/ProgramsPreview.tsx:160:22" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="160" data-component-end-line="164" data-component-file="ProgramsPreview.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                        className={`tag-pill ${prog.tagColor}`}
                      >
                        {prog.tag}
                      </span>
                    </div>
                  )}
                </div>


                {/* Content */}
                <div data-component-id="src/app/components/ProgramsPreview.tsx:171:16" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="171" data-component-end-line="209" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-7%22%7D" className="p-7">

                  <div data-component-id="src/app/components/ProgramsPreview.tsx:173:18" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="173" data-component-end-line="190" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-start%20mb-4%22%7D" className="flex justify-between items-start mb-4">

                    <div data-component-id="src/app/components/ProgramsPreview.tsx:175:20" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="175" data-component-end-line="183" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                      <h3 data-component-id="src/app/components/ProgramsPreview.tsx:176:22" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="176" data-component-end-line="178" data-component-file="ProgramsPreview.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-serif%20text-3xl%20text-foreground%22%7D" className="font-serif text-3xl text-foreground">
                        {prog.title}
                      </h3>

                      <p data-component-id="src/app/components/ProgramsPreview.tsx:180:22" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="180" data-component-end-line="182" data-component-file="ProgramsPreview.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mt-1%22%7D" className="text-sm text-muted-foreground mt-1">
                        {prog.subtitle}
                      </p>
                    </div>


                    <p data-component-id="src/app/components/ProgramsPreview.tsx:186:20" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="186" data-component-end-line="188" data-component-file="ProgramsPreview.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%20text-primary%22%7D" className="text-sm font-medium text-primary">
                      {prog.duration}
                    </p>

                  </div>


                  <p data-component-id="src/app/components/ProgramsPreview.tsx:193:18" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="193" data-component-end-line="195" data-component-file="ProgramsPreview.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-relaxed%20text-muted-foreground%20mb-6%22%7D" className="text-sm leading-relaxed text-muted-foreground mb-6">
                    {prog.description}
                  </p>


                  <div data-component-id="src/app/components/ProgramsPreview.tsx:198:18" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="198" data-component-end-line="207" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
                    {prog.highlights.map((item) => (
                      <span data-component-id="src/app/components/ProgramsPreview.tsx:200:22" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="200" data-component-end-line="205" data-component-file="ProgramsPreview.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20px-3%20py-1.5%20rounded-full%20border%20border-border%20text-muted-foreground%22%7D"
                        key={item}
                        className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground"
                      >
                        {item}
                      </span>
                    ))}
                  </div>

                </div>

              </div>



              {/* Coming Soon Overlay */}
              {prog.comingSoon && (
                <div data-component-id="src/app/components/ProgramsPreview.tsx:217:16" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="217" data-component-end-line="235" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%20bg-background%2F40%20backdrop-blur-md%22%7D" className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-md">

                  <div data-component-id="src/app/components/ProgramsPreview.tsx:219:18" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="219" data-component-end-line="233" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20px-6%22%7D" className="text-center px-6">

                    <h3 data-component-id="src/app/components/ProgramsPreview.tsx:221:20" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="221" data-component-end-line="223" data-component-file="ProgramsPreview.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-serif%20text-4xl%20text-foreground%20tracking-tight%22%2C%22textContent%22%3A%22Coming%20Soon%22%7D" className="font-serif text-4xl text-foreground tracking-tight">
                      Coming Soon
                    </h3>


                    <div data-component-id="src/app/components/ProgramsPreview.tsx:226:20" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="226" data-component-end-line="226" data-component-file="ProgramsPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-px%20bg-border%20mx-auto%20my-5%22%7D" className="w-12 h-px bg-border mx-auto my-5" />


                    <p data-component-id="src/app/components/ProgramsPreview.tsx:229:20" data-component-path="src/app/components/ProgramsPreview.tsx" data-component-line="229" data-component-end-line="231" data-component-file="ProgramsPreview.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22B.Pharm%20admissions%20will%20open%20shortly%22%7D" className="text-sm text-muted-foreground">
                      B.Pharm admissions will open shortly
                    </p>

                  </div>

                </div>
              )}

            </div>
          ))}
        </div>

      </div>
    </section>
  );
}